import { GitBranch, Milestone } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { EmptyState } from '@vnetwork/ui/data';
import { Badge, Card, CardBody, Skeleton, Button } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { AccountBreadcrumbs } from './components/account-breadcrumbs';
import { useCurrentEmployee } from '@/api/account';
import { useCareerPaths, type CareerPathStageRow } from '@/api/account-career-paths';
import { formatDate } from '@/lib/format';

/**
 * Working Process — the signed-in employee's career-path stage timeline
 * (intern → probation → official → offboard) with dates and notes.
 *
 * Real data: resolve `employee_id` via `useCurrentEmployee`, then
 * `GET /employees/:employeeID/career-paths` (see `@/api/account-career-paths`).
 * Read-only; stage transitions/edits are HR-admin actions elsewhere.
 */

const STAGE_LABEL: Record<string, string> = {
  intern: 'Intern',
  probation: 'Probation',
  official: 'Official',
  offboard: 'Offboard',
};

const STAGE_TONE: Record<string, 'info' | 'warning' | 'success' | 'neutral'> = {
  intern: 'info',
  probation: 'warning',
  official: 'success',
  offboard: 'neutral',
};

export default function WorkingProcessPage() {
  const { data: me } = useCurrentEmployee();
  const employeeId = me?.employee_id;
  const { data: stages, isLoading, isError, refetch } = useCareerPaths(employeeId);

  return (
    <Container size="full">
      <PageHeader
        breadcrumbs={<AccountBreadcrumbs current="Working Process" />}
        title="Working Process"
        description="Your career-path stages and employment timeline"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {isError ? (
          <Alert
            tone="danger"
            title="Failed to load working process"
            actions={
              <Button size="sm" variant="ghost" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : isLoading || !employeeId ? (
          <Card flat>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 64 }} />
              ))}
            </CardBody>
          </Card>
        ) : stages && stages.length > 0 ? (
          stages.map((stage) => <CareerPathStageCard key={stage.career_path_id} stage={stage} />)
        ) : (
          <EmptyState
            icon={<Milestone size={24} />}
            title="No career-path record"
            description="No employment stages have been recorded yet."
          />
        )}
      </div>
    </Container>
  );
}

/** One career-path stage rendered as a bordered card with stage badge, date range, and note. */
function CareerPathStageCard({ stage }: { stage: CareerPathStageRow }) {
  const label = STAGE_LABEL[stage.career_path_stage] ?? stage.career_path_stage;
  const tone = STAGE_TONE[stage.career_path_stage] ?? 'neutral';
  const start = formatDate(stage.career_path_start_date);
  const end = stage.career_path_end_date ? formatDate(stage.career_path_end_date) : 'Present';

  return (
    <Card>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
          <Badge tone={tone} appearance="subtle" startIcon={<GitBranch size={13} />}>
            {label}
          </Badge>
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            {start} — {end}
          </span>
        </div>

        {stage.career_path_note ? (
          <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-default)' }}>
            {stage.career_path_note}
          </p>
        ) : null}

        {stage.notes && stage.notes.length > 0 ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            {stage.notes.map((note, i) => (
              <div
                key={i}
                style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}
              >
                • {note.note_content}
                {note.created_by_name ? (
                  <span style={{ color: 'var(--vn-fg-subtle)' }}> — {note.created_by_name}</span>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
